// What we know about a computation before looking at any data.
//
// The insights panel derives four facts about a column on every render, from the only
// four fields the form-fields endpoint returns (`subject`, `type`, `context`, `tags`):
//
//   - is this usable in a model at all? Nothing filters, so `company__twitter__avatar`
//     (an image URL) is offered as a split candidate.
//   - is it multi-valued? Inferred from `coverage > 1.01`, an arithmetic heuristic.
//   - is it an ordered scale, and in what order? Not known at all, so `bucketStats`
//     sorts by conversion rate and a "candidate" can be a non-contiguous set of bands.
//   - is this candidate the same signal as that one? Not known, so several views of one
//     measurement can fill the shortlist.
//
// All four are properties of the computation rather than of the node, so they belong in
// a table. This is that table, and it is the SAME table rgip-copilot's engine reads
// (`app/sources/springbok/dictionary.py`). Both sides reading one artifact is what stops
// the two engines drifting on these four facts, which is the thing the cross-repo parity
// harness exists to catch.
//
// Only the 634 standard computations plus the 13 created for every tenant are in here.
// They are byte-identical on all 119 production tenants, which is what makes shipping
// them as a static asset correct rather than a cache. A tenant's OWN computation is
// absent, and every lookup below answers "unknown" for it so today's inference still runs.
//
// PROTOTYPE: the JSON is a generated snapshot. Storage and versioning are deliberately
// unsolved; expect it to be regenerated often.

// Suffixed `.data` deliberately: webpack's resolve.extensions lists `.json` BEFORE `.ts`
// (config/webpack.config.ts/resolvers.ts), so a data file named `computationDictionary.json`
// shadows this module and `import { declaredOrder } from './computationDictionary'` silently
// resolves to the raw object, which has no functions on it.
import table from './computationDictionary.data.json';

/** The compact on-disk shape. Keys are short because this ships in the bundle. */
interface Entry {
  /** label */
  l?: string;
  /** description, one sentence */
  d?: string;
  /** type: o ordinal, m multi-valued, b boolean, n numeric, c categorical, i id, f free text, D date */
  t?: string;
  /** the ordered levels, lowest first */
  o?: string[];
  /** why it must never be a split candidate */
  x?: string;
  /** the measurement this is a view of */
  m?: string;
  /** the dimension it measures */
  f?: string;
  /** the value missing data is stored as, when it is unrecoverable */
  z?: string;
}

const DICTIONARY = table as unknown as Record<string, Entry>;

const entry = (column: string): Entry => DICTIONARY[column] ?? {};

/**
 * Why this column must never be offered as a split, or undefined when it may be.
 *
 * Identity columns, pipeline status fields, free text, coordinates, social vanity counts
 * and dead feeds. A column absent from the dictionary is NOT excluded: a tenant's own
 * column is unknown here and has to keep working.
 */
export function exclusionReason(column: string): string | undefined {
  return entry(column).x;
}

/**
 * The levels of an ordered scale, lowest first, as declared rather than inferred.
 *
 * Fewer than three levels is not a scale: two buckets are a plain binary split and any
 * grouping of them is already contiguous.
 */
export function declaredOrder(column: string): string[] | undefined {
  const order = entry(column).o;
  return Array.isArray(order) && order.length >= 3 ? order : undefined;
}

/**
 * True when one record carries SEVERAL values, undefined when the column is unknown.
 *
 * `undefined` means "fall back to the coverage heuristic". `false` is an assertion that
 * the column holds one value and the heuristic must not overrule it.
 */
export function isMultiValued(column: string): boolean | undefined {
  const e = entry(column);
  return e.t === undefined ? undefined : e.t === 'm';
}

/**
 * True when the dictionary knows this column's values have NO order.
 *
 * A categorical or a flag. Not a numeric, which is ordered even without a declared level
 * list, and not an unknown column, where nothing is known either way. This is what
 * separates "Software vSMB or better", which asserts a ranking of distinct categories that
 * does not exist, from a plain membership list.
 */
export function isUnordered(column: string): boolean {
  const type = entry(column).t;
  return type === 'c' || type === 'b';
}

/** The name a marketer should read, written once and reviewed. */
export function labelOf(column: string): string | undefined {
  return entry(column).l;
}

/** One sentence saying what the field measures. */
export function descriptionOf(column: string): string | undefined {
  return entry(column).d;
}

/**
 * The bucket value that missing data is stored as, when it cannot be told apart from a
 * real one. `undefined` when the column does not do this, or is unknown here.
 *
 * 287 standard computations do. `COALESCE(hg__tech_crm_count, 0)` puts "runs no CRM" and
 * "HG has no technographic coverage for this company" in one bucket, and unlike a `-333`
 * sentinel there is no value left to exclude: the information is gone at computation time.
 *
 * Compare it against a bucket's `rawName`, never its display name. `hg__has_crm_tech`
 * labels its buckets `Yes` and `No` while holding `1` and `0`, so a name comparison
 * matches nothing on the family this exists for.
 *
 * An empty string is a real answer (`tech`, `tags`), so test with `!== undefined` rather
 * than for truthiness.
 */
export function nullConflatedWith(column: string): string | undefined {
  return entry(column).z;
}
